/* List all the type from the realtime database for the chat feature */
/*
chatChannels
| <channelID>:
-| title
-| image
-| type:'direct'|'listing'|'event'
-| listing
-| booking
-| event
-| latestMessage
--| ChatMessage+id
-| members:
--| <userId>:
---| role
-| typing:
--| <userId>:boolean

chatMessages
| <channelId>:
-| <messageId>:
--| type
--| timestamp
--| sender
--| message
--| imageUrl?
--| read
---| <userId>:boolean

chatUsers
| <userId>
-| avatar
-| displayName
-| email?
-| status:
--| lastChanged
--| state

userChannels
| <userId>
-| <channelId>
--| lastUpdate
--| unseenCount
--| draft?
--| muted?
*/

import { Booking } from "./bookings";
import { DatabaseTimestamp as Timestamp } from "./common";
import { EUserRole } from "./user";

/** 
 * The realtime db root name for info on all channels. Object key is the channelID.
 * @see ChatChannels type of the info
 */
export const CHAT_CHANNELS = "chatChannels";

/**
 * The realtime db name for the chat channel latest message
 * @see CHAT_CHANNELS
 * @see ChatChannelLatestMessage type of the info in it
 * @see ChatChannel type of the parent info
 */
export const CHAT_CHANNEL_LATEST_MESSAGE = "lastMessage";

/**
 * The realtime db name for the chat channel members
 * @see CHAT_CHANNELS
 * @see ChatChannel type of the parent info
 */
export const CHAT_CHANNEL_MEMBERS = "members";

/**
 * The realtime db name for the chat channel members currently typing
 * @see CHAT_CHANNELS
 * @see ChatChannel type of the parent info
 */
export const CHAT_CHANNEL_TYPING = "typing";

/**
 * The realtime db name for the chat channel medias (medias sent in the channel)
 * @see CHAT_CHANNELS
 * @see ChatChannel type of the parent info
 */
export const CHAT_CHANNEL_MEDIAS = "medias";

export type ChatChannelLatestMessage = ChatMessage & { id: string };

/** The info of a channel */
export type ChatChannel = {
    /** The members of the channel, key is the member's user ID */
    [CHAT_CHANNEL_MEMBERS]: { [userId: string]: ChatChannelMember };
    /** The members currently typing, key is the member's user ID */
    [CHAT_CHANNEL_TYPING]: { [userId: string]: boolean }
    /** The last message in the channel */
    [CHAT_CHANNEL_LATEST_MESSAGE]?: ChatChannelLatestMessage;
    /** The medias shared in the channel */
    [CHAT_CHANNEL_MEDIAS]: { [mediaId: string]: ChatChannelMedia }
    /** The title of the channel */
    title: string;
    /** The image of the channel (user avatar/listing picture/event photo/...) */
    image: string;
    /** The type of the discussion */
    type: 'direct' | 'listing' | 'event';
}
    & (
        {
            type: 'direct'
        }
        | {
            type: 'listing',
            /** The listing ID the channel relate to */
            listing: string;
            /** Optional, The booking ID the channel relate to */
            booking?: string;
        }
        | {
            type: 'event',
            /** The event ID the channel relate to */
            event: string;
            /** Optional, The booking ID the channel relate to */
            booking?: string;
        }
    );

export type ChatChannelMember = {
    /** The role of the user in the channel, can be undefined for the case of direct message or external users */
    role?: EUserRole;
    /** The state indicating the user status in the channel (this is needed to still link to ChatUsers info for the thread content) 
     * 'invited': Has been invited in the channel (but not yet accepted)
     * 'joined': Is currently present in the channel (default, same as undefined)
     * 'left': Was present in the channel in the past but has left since (but can be re-invited in)
     * 'deleted': The user has deleted his account
     * 'banned': The user has been banned from this channel and cannot be reinvited in
    */
    state?: 'invited' | 'joined' | 'deleted' | 'left' | 'banned';
}

export type ChatChannelMedia = {
    /** The path of the media in the storage */
    path: string
    /** The url of the media */
    url: string
    /** The size of the media */
    size: number
    /** The user id of the person who uploaded it */
    uploadedBy: string
    /** The date of the upload */
    uploadedAt: Timestamp
}

/** The ChatChannels is an Object with key as channelID */
export type ChatChannels = {
    [channelId: string]: ChatChannel;
}

/** 
 * The realtime db root name for info on all channel messages. Object key is the channelID then messageID.
 * @see ChatMessages type of the info
 */
export const CHAT_MESSAGES = "chatMessages";

/** The ChatMessage is representing the data in the realtime db for a chat message */
export type ChatMessage = {
    /** The type of the message */
    type: "text" | "image" | "system";
    /** The sender's userID */
    sender: string;
    /** The timestamp of the message */
    timestamp: Timestamp;
    /** The string value of the message */
    message: string;
    /** Optional imageUrl in the message */
    image?: string;
    /** The user(s) who read this message (useful for "Read by {user}" message) */
    read: { [userId: string]: boolean }
}

/** The ChatMessages is an Object with key as messageID */
export type ChatMessages = {
    [channelId: string]: {
        [messageId: string]: ChatMessage;
    }
}

/** 
 * The realtime db root name for info on all channels the user is part of. Object key is the userID.
 * @see UserChannels type of the info
 */
export const USER_CHANNELS = "userChannels";

/**
 * The realtime db name for the user channel unread/unseen count
 * @see UserChannel
 */
export const USER_CHANNEL_UNSEEN_COUNT = "unseenCount";

/**
 * The realtime db name for the user channel last update
 * @see UserChannel
 */
export const USER_CHANNEL_LAST_UPDATE = "lastUpdate";

/** The user's channel information */
export type UserChannel = {
    /** Any draft message left by the user for this channel */
    draft?: Pick<ChatMessage, 'message' | 'image'>;
    /** The number of unseen message for this user on the channel */
    [USER_CHANNEL_UNSEEN_COUNT]: any;//number | object;//number for reading object for database increment
    /** The timestamp of the last update in the channel (allow to sort by most recent) */
    [USER_CHANNEL_LAST_UPDATE]: Timestamp;
    /** Boolean indicating if the user has muted any notification coming for this channel */
    muted?: boolean;
}

/** The UserChannels is an Object with key as channelID */
export type UserChannels = {
    [userId: string]: {
        [channelId: string]: UserChannel;
    }
}

/**
 * The realtime db root name for info on all users to be used for chat. Object key is the userID.
 * Those are very basic information only to be used in the chat feature.
 * @see ChatUsers type of the info
 * @see ChatUser type of the user info
 */
export const CHAT_USERS = "chatUsers";

/** 
 * The realtime db filed name for user status.
 * @see ChatUser where used
 * @see ChatUserStatus type of the info
 */
export const CHAT_USERS_STATUS = "status";

export type ChatUser = {
    /** The display name of the user */
    displayName: string;
    /** The avatar url of the user */
    avatar: string;
    /** (Optional) The email of the user (for sending notification) */
    email?: string;
    /** The status of the user (presence) */
    [CHAT_USERS_STATUS]: ChatUserStatus;
}

export type ChatUserStatus = {
    /**
     * The state of the user:
     * - 'new': brand new user (will be updated by the connection of the user device)
     * - 'online': the user is currently online on the platform
     * - 'offline': user user haven't been on the platform for a while and appear offline in the chat
     * - 'deleted': minimum data is kept from a deleted account to still show the user but can't redirect to profile or private message
     */
    state: 'new' | 'online' | 'offline' | 'deleted';
    /** The last time the status was changed */
    lastChanged: Timestamp;
}

/** The ChatUsers is an Object with key as userId */
export type ChatUsers = {
    [userId: string]: ChatUser;
}

/**
 * The realtime db root name for info on all listings to be used for chat. Object key is the listingID.
 * Those are very basic information only to be used in the chat feature.
 * @see ChatListing type of the info
 */
export const CHAT_LISTINGS = "chatListings";
/** The current occupants of the listing (it will be updated with host, current guests and current cohost) */
export const CHAT_LISTING_OCCUPANTS = "currentOccupants";

/** The current occupant in a listing */
export type ChatListingOccupant = { role: EUserRole };

/** The listing info for the chat functionality */
export type ChatListing = {
    /** The chat channel linked to this listing */
    chatChannelId: string;
    /** The name of the listing to display */
    name: string;
    /** The main image of the listing */
    image: string;
    /** The list of current occupants and their role in the listing */
    [CHAT_LISTING_OCCUPANTS]: {
        [userId: string]: ChatListingOccupant;
    }
}

/** The ChatListings is an Object with key as listingId */
export type ChatListings = {
    [listingId: string]: ChatListing;
}

/**
 * The realtime db root name for info on all bookings to be used for chat. Object key is the bookingID.
 * Those are very basic information only to be used in the chat feature.
 * @see ChatBooking type of the info
 */
export const CHAT_BOOKINGS = "chatBookings";

/** The booking info for the chat functionality */
export type ChatBooking = Pick<Booking, 'type' | 'status'> & {
    /** The chat channel linked to this booking */
    chatChannelId: string;
    /** The dates of the booking */
    dates: [Timestamp, Timestamp];
}

/** The ChatBookings is an Object with key as bookingId */
export type ChatBookings = {
    [bookingId: string]: ChatBooking;
}

/**
 * TODO to complete when event feature developed
 * The realtime db root name for info on all events to be used for chat. Object key is the eventID.
 * Those are very basic information only to be used in the chat feature.
 * @see ChatEvent type of the info
 */
export const CHAT_EVENTS = "chatEvents";

export const CHAT_EVENT_PARTICIPANTS = "participants";
export type ChatEventParticipant = { role: EUserRole };//maybe different roles and a rsvp (auto invite people in the listing)

/** The event info for the chat functionality */
export type ChatEvent = {
    /** The chat channel linked to this event */
    chatChannelId: string;
    /** The name of the event to display */
    name: string;
    /** The main image of the event */
    image?: string;
    /** The date/time of the event */
    dates: [Timestamp, Timestamp];
    /** The list of current participants and their role in the event */
    [CHAT_EVENT_PARTICIPANTS]: {
        [userId: string]: ChatEventParticipant;
    }
}

/** The ChatEvents is an Object with key as eventId */
export type ChatEvents = {
    [eventId: string]: ChatEvent;
}

/**
 * The realtime db root name for index on all direct/1-1 message to be used for chat. 
 * Object key is the users' ID sorted `userId1-userId2`.
 * Those are very basic information only to be used in the chat feature.
 * @see ChatListing type of the info
 */
export const CHAT_DIRECT_INDEX = "directChatIndex";

export type DirectChatIndex = {
    // Helper index to find existing direct chats between two users
    [userIds: string]: string, // always use sorted userIds:chatRoomId
}
